import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { Observable, of, Subject } from 'rxjs';

import { SchedulesComponent } from './schedules.component';

/**
 * Prompts the user before unsaved schedule edits are dropped (#7275).
 *
 * Shared by LeaveScheduleGuard (route deactivation) and SchedulesComponent
 * (view/tab switches) so the dialog copy stays in one place. Emits `true`
 * when the user chooses "Continue and Discard", `false` for
 * "Return To Editing"; completes after the first answer.
 */
export function confirmUnsavedScheduleChanges(
  confirmService: ConfirmationService
): Observable<boolean> {
  const sub = new Subject<boolean>();

  confirmService.confirm({
    message: 'You have unsaved changes. If you continue all unsaved changes will be discarded.',
    acceptLabel: 'Continue and Discard',
    rejectLabel: 'Return To Editing',
    header: 'Discard Changes?',
    icon: 'pi pi-exclamation-triangle',
    closeOnEscape: true,
    dismissableMask: true,
    acceptButtonStyleClass: 'p-button-danger',
    acceptIcon: 'pi pi-trash',
    accept: async () => {
      sub.next(true);
    },
    reject: (_eventCode) => {
      sub.next(false);
    },
  });

  return sub.asObservable();
}

@Injectable({
  providedIn: 'root'
})
export class LeaveScheduleGuard  {

  constructor(
    private confirmService: ConfirmationService
  ) { }

  canDeactivate(
    component: SchedulesComponent,
    _currentRoute: ActivatedRouteSnapshot,
    _currentState: RouterStateSnapshot,
    nextState?: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {

    // CR 6317bed0 (!2637): tab switches between the :viewType views are
    // already confirmed by SchedulesComponent.changeView() — running this
    // guard for them too prompts twice, and the reject's URL heal could
    // prompt a third time. CR 418247a0: the bypass covers ONLY the three
    // views that reuse SchedulesComponent (the schedule tabs: day/week/
    // month, see SchedulePageComponent.tabs). dispatch/map/ops-readiness
    // are sibling routes with DIFFERENT components — navigating there
    // destroys SchedulesComponent and its latestEventEdits, so they still
    // get this prompt. The guard stays on the ':viewType' child route so
    // `component` is SchedulesComponent (the parent never owned the edit
    // state) and every departure that destroys the schedule UI is covered.
    const SCHEDULE_VIEW_TABS = [ 'day', 'week', 'month' ];
    const nextUrl = nextState?.url ?? '';
    const nextTab = nextUrl.startsWith('/schedule/') ? nextUrl.split('/')[2]?.split(';')[0] : undefined;
    if (nextTab && SCHEDULE_VIEW_TABS.includes(nextTab)) {
      return of(true);
    }

    if (!component.latestEventEdits?.length) {
      return of(true);
    }

    return confirmUnsavedScheduleChanges(this.confirmService);
  }

}
